import { useRef, useState } from "react";
import { LAYOUT_ORDER, LAYOUTS } from "../data/layouts";
import { DEFAULT_CROP, emptyArtworkPage } from "../data/defaults";
import { uploadImage, releaseImage } from "./images";
import { BRAND_OFFSET_MAX, BRAND_OFFSET_MIN } from "../document/pages/SuggestedArtworkPage";

const BRAND_STEP = 10; // pt لكل ضغطة

// ↑ ↓ للشعار و«ارتيكا» (صفحات اللوحات + الغلاف) - للمدير بس
export function BrandOffsetControl({ offset, min, max, onChange }) {
    return (<div className="admin-settings-row">
      <span>الشعار و«ارتيكا»</span>
      <button type="button" className="icon" title="صعود" disabled={offset <= min} onClick={() => onChange(Math.max(min, offset - BRAND_STEP))}>↑</button>
      <button type="button" className="icon" title="نزول" disabled={offset >= max} onClick={() => onChange(Math.min(max, offset + BRAND_STEP))}>↓</button>
      {offset !== 0 && (<button type="button" className="link" onClick={() => onChange(0)}>
          مكانه الأصلي
        </button>)}
    </div>);
}

// 🔧 إعدادات الصفحة للمدير بس: خلفية الصفحة (صورة فقط) + رفع/تنزيل الشعار
// و«ارتيكا». الباك اند يحفظها من المدير بس (proposalController.js)
function AdminPageSettings({ page, wall, trackUpload, onPatch }) {
    const input = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState("");
    const offset = Number(page.brandOffset) || 0;
    const pickBackground = async (file) => {
        if (!file)
            return;
        if (!file.type.startsWith("image/")) {
            setError("الخلفية لازم تكون صورة");
            return;
        }
        setError("");
        setUploading(true);
        try {
            const background = await trackUpload(uploadImage(file));
            onPatch(() => ({ background }));
        }
        catch {
            setError("تعذّر رفع الخلفية، حاول مرة ثانية");
        }
        finally {
            setUploading(false);
        }
    };
    return (<div className="admin-settings">
      <div className="admin-settings-title">🔧 إعدادات المدير</div>
      <div className="admin-settings-row">
        <span>الخلفية</span>
        <button type="button" className="link" disabled={uploading} onClick={() => input.current?.click()}>
          {uploading ? "جارٍ الرفع..." : page.background ? "تغيير الصورة" : "رفع صورة"}
        </button>
        {page.background && (<button type="button" className="link danger" onClick={() => onPatch(() => ({ background: null }))}>
            رجوع لخلفية القالب
          </button>)}
        <input ref={input} type="file" accept="image/*" hidden onChange={(e) => {
            pickBackground(e.target.files?.[0]);
            e.target.value = "";
        }}/>
      </div>
      {error && <div className="field-error">{error}</div>}
      {wall && (<BrandOffsetControl offset={offset} min={BRAND_OFFSET_MIN} max={BRAND_OFFSET_MAX} onChange={(brandOffset) => onPatch(() => ({ brandOffset }))}/>)}
    </div>);
}
const SLOT_NAMES = {
    1: ["اللوحة"],
    3: ["يمين", "وسط", "يسار"],
    4: ["1 (يمين)", "2", "3", "4 (يسار)"],
};
// also used by the project pages editor (ProjectEditor.jsx)
export function SlotEditor({ slot, label, mode, onChange, onUploaded, trackUpload }) {
    const input = useRef(null);
    const [error, setError] = useState("");
    const [uploading, setUploading] = useState(false);
    const pick = async (file) => {
        if (!file)
            return;
        if (!file.type.startsWith("image/")) {
            setError("الملف ليس صورة");
            return;
        }
        setError("");
        setUploading(true);
        try {
            // the upload takes a while: the result is applied to the latest
            // proposal state (by page id), not to this render's snapshot
            const image = await trackUpload(uploadImage(file));
            onUploaded(image);
        }
        catch {
            setError("تعذّر رفع الصورة، حاول مرة ثانية");
        }
        finally {
            setUploading(false);
        }
    };
    const setCrop = (patch) => onChange({ ...slot, crop: { ...slot.crop, ...patch } });
    return (<div className="slot">
      <div className={`slot-drop ${slot.image ? "has-image" : ""}`} aria-busy={uploading} onClick={() => !uploading && input.current?.click()} onDragOver={(e) => e.preventDefault()} onDrop={(e) => {
            e.preventDefault();
            if (!uploading)
                pick(e.dataTransfer.files[0]);
        }} role="button" tabIndex={0} onKeyDown={(e) => !uploading && (e.key === "Enter" || e.key === " ") && input.current?.click()}>
        {uploading ? <span>جارٍ الرفع...</span> : slot.image ? <img src={slot.image.src} alt=""/> : <span>+ رفع صورة</span>}
        <input ref={input} type="file" accept="image/*" hidden onChange={(e) => pick(e.target.files?.[0] ?? undefined)}/>
      </div>
      <div className="slot-meta">
        <div className="slot-label">{label}</div>
        {slot.image && (<div className="slot-file" title={slot.image.name}>
            {slot.image.width}×{slot.image.height}px
          </div>)}
        {error && <div className="field-error">{error}</div>}
        {slot.image && (<div className="crop">
            <label>
              تكبير
              <input type="range" min={1} max={mode === "frame" ? 3 : 1.5} step={0.005} value={slot.crop.zoom} onChange={(e) => setCrop({ zoom: +e.target.value })}/>
            </label>
            {mode === "frame" && (<label>
                أفقي
                <input type="range" min={0} max={1} step={0.01} value={1 - slot.crop.x} onChange={(e) => setCrop({ x: 1 - +e.target.value })}/>
              </label>)}
            <label>
              عمودي
              <input type="range" min={0} max={1} step={0.01} value={slot.crop.y} onChange={(e) => setCrop({ y: +e.target.value })}/>
            </label>
          </div>)}
        {slot.image && (<div className="slot-actions">
            <button type="button" className="link" onClick={() => setCrop({ ...DEFAULT_CROP })}>
              إعادة القص
            </button>
            <button type="button" className="link danger" onClick={() => {
                releaseImage(slot.image);
                onChange({ image: null, crop: { ...DEFAULT_CROP } });
            }}>
              إزالة
            </button>
          </div>)}
      </div>
    </div>);
}
/** onChange accepts the new pages, or an updater (prev => next) for async upload results. */
// isAdmin: يظهر إعدادات المدير (الخلفية/مكان الشعار) لكل صفحة
export function ArtworksEditor({ pages, onChange, onLocate, trackUpload, isAdmin }) {
    const [newLayout, setNewLayout] = useState("wall-portrait");
    const update = (index, page) => onChange(pages.map((p, i) => (i === index ? page : p)));
    const move = (index, delta) => {
        const target = index + delta;
        if (target < 0 || target >= pages.length)
            return;
        const next = [...pages];
        [next[index], next[target]] = [next[target], next[index]];
        onChange(next);
    };
    const remove = (index) => {
        pages[index].slots.forEach((s) => releaseImage(s.image));
        onChange(pages.filter((_, i) => i !== index));
    };
    const changeLayout = (index, layout) => {
        const page = pages[index];
        const count = LAYOUTS[layout].slots.length;
        // keep already uploaded images where possible
        const slots = Array.from({ length: count }, (_, i) => page.slots[i] ?? { image: null, crop: { ...DEFAULT_CROP } });
        page.slots.slice(count).forEach((s) => releaseImage(s.image));
        update(index, { ...page, layout, slots: slots.map((s) => ({ ...s, crop: { ...DEFAULT_CROP } })) });
    };
    return (<div className="artworks">
      {pages.map((page, index) => {
            const layout = LAYOUTS[page.layout];
            const names = SLOT_NAMES[layout.slots.length] ?? layout.slots.map((_, i) => `${i + 1}`);
            return (<div className="art-card" key={page.id}>
            <div className="art-card-head">
              <span className="art-index">{index + 1}</span>
              <select value={page.layout} onChange={(e) => changeLayout(index, e.target.value)} aria-label="تصميم الصفحة">
                {LAYOUT_ORDER.map((id) => (<option key={id} value={id}>
                    {LAYOUTS[id].label}
                  </option>))}
              </select>
              <div className="art-card-tools">
                <button type="button" className="icon" title="عرض في المعاينة" onClick={() => onLocate(page.id)}>
                  ◎
                </button>
                <button type="button" className="icon" title="تحريك للأعلى" disabled={index === 0} onClick={() => move(index, -1)}>
                  ↑
                </button>
                <button type="button" className="icon" title="تحريك للأسفل" disabled={index === pages.length - 1} onClick={() => move(index, 1)}>
                  ↓
                </button>
                <button type="button" className="icon danger" title="حذف الصفحة" onClick={() => remove(index)}>
                  ×
                </button>
              </div>
            </div>
            <div className={`slots slots-${layout.slots.length}`}>
              {layout.slots.map((sizing, i) => (<SlotEditor key={i} label={names[i]} slot={page.slots[i]} mode={sizing.kind} onChange={(slot) => update(index, { ...page, slots: page.slots.map((s, j) => (j === i ? slot : s)) })} trackUpload={trackUpload} onUploaded={(image) => onChange((prev) => prev.map((p) => p.id === page.id
                    ? { ...p, slots: p.slots.map((s, j) => (j === i ? { image, crop: { ...DEFAULT_CROP } } : s)) }
                    : p))}/>))}
            </div>
            {page.layout === "option" && (<>
                <label className="field compact">
                  <span>عنوان الخيار (مثال: منظر طبيعي تجريدي) - يطلع تحت «الخيار {pages.slice(0, index + 1).filter((p) => p.layout === "option").length}»</span>
                  <input value={page.caption || ""} maxLength={200} onChange={(e) => update(index, { ...page, caption: e.target.value })}/>
                </label>
                <label className="field compact">
                  <span>وصف الخيار</span>
                  <textarea rows={3} value={page.description || ""} maxLength={1000} onChange={(e) => update(index, { ...page, description: e.target.value })}/>
                </label>
              </>)}
            {layout.hasCaption && (<label className="field compact">
                <span>عنوان الصفحة (مثال: ورق جدار) - يطلع تحت اللوحة</span>
                <input value={page.caption} onChange={(e) => update(index, { ...page, caption: e.target.value })}/>
              </label>)}
            {isAdmin && page.layout !== "option" && (<AdminPageSettings page={page} wall={layout.background === "wall"} trackUpload={trackUpload} onPatch={(patch) => onChange((prev) => prev.map((p) => (p.id === page.id ? { ...p, ...patch(p) } : p)))}/>)}
          </div>);
        })}

      <div className="add-page">
        <select value={newLayout} onChange={(e) => setNewLayout(e.target.value)} aria-label="تصميم الصفحة الجديدة">
          {LAYOUT_ORDER.map((id) => (<option key={id} value={id}>
              {LAYOUTS[id].label}
            </option>))}
        </select>
        <button type="button" className="btn" onClick={() => onChange([...pages, emptyArtworkPage(newLayout)])}>
          + إضافة صفحة لوحات
        </button>
      </div>
    </div>);
}
